<template>
  <div class="bg-white rounded-lg border border-gray-200 p-4 mb-6">
    <h3 class="text-sm font-semibold mb-3 text-gray-900">
      Step 3: Map Columns to Fields
    </h3>

    <div class="space-y-4">
      <!-- Regular Fields -->
      <div>
        <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Fields</h4>
        <div class="space-y-1.5">
          <div
            v-for="field in regularFields"
            :key="field.name"
            class="grid grid-cols-3 gap-4 items-center px-3 py-2 bg-gray-50 rounded"
          >
            <div>
              <span class="text-sm font-medium text-gray-900">{{ field.name }}</span>
              <span v-if="field.required" class="text-red-500 ml-1">*</span>
              <div class="text-xs text-gray-500">{{ field.type }}</div>
            </div>
            <div class="col-span-2">
              <MappedSelect
                :model-value="fieldMappings[field.name]"
                :options="sheetColumns"
                placeholder="-- No mapping --"
                @update:model-value="updateFieldMapping(field.name, $event)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Foreign Key Fields -->
      <div v-if="foreignKeyFields.length > 0">
        <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Foreign keys</h4>
        <div class="space-y-3">
          <div
            v-for="field in foreignKeyFields"
            :key="field.name"
            class="p-3 bg-gray-50 rounded border border-gray-100"
          >
            <div class="mb-2">
              <span class="text-sm font-medium text-gray-900">{{ field.name }}</span>
              <span v-if="field.required" class="text-red-500 ml-1">*</span>
              <span class="text-xs text-gray-500 ml-2">→ {{ field.related_model }}</span>
            </div>

            <!-- Entity FKs resolve via Identifier lookup; others match a field
                 on the related model directly. -->
            <div class="space-y-3">
              <div class="grid grid-cols-2 gap-3">
                <MappedSelect
                  :label="field.related_model === 'Entity' ? 'Identifier column' : 'Sheet column'"
                  :model-value="foreignKeyMappings[field.name]?.sheet_column"
                  :options="sheetColumns"
                  placeholder="-- Select column --"
                  @update:model-value="updateForeignKeyMapping(field.name, 'sheet_column', $event)"
                />
                <MappedSelect
                  v-if="field.related_model === 'Entity'"
                  label="Identifier type"
                  :model-value="foreignKeyMappings[field.name]?.identifier_type"
                  :options="identifierTypes"
                  placeholder="-- Select type --"
                  @update:model-value="updateForeignKeyMapping(field.name, 'identifier_type', $event)"
                />
                <MappedSelect
                  v-else
                  :label="`Match ${field.related_model} field`"
                  :model-value="foreignKeyMappings[field.name]?.model_field"
                  :options="relatedFieldOptions(field.name)"
                  placeholder="-- Select field --"
                  @focus="$emit('load-related-fields', field.related_model, field.name)"
                  @update:model-value="updateForeignKeyMapping(field.name, 'model_field', $event)"
                />
              </div>

              <FuzzyMatchControl
                :enabled="foreignKeyMappings[field.name]?.use_fuzzy_matching || false"
                :threshold="foreignKeyMappings[field.name]?.fuzzy_threshold || 80"
                @update:enabled="updateForeignKeyMapping(field.name, 'use_fuzzy_matching', $event)"
                @update:threshold="updateForeignKeyMapping(field.name, 'fuzzy_threshold', $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MappedSelect from './controls/MappedSelect.vue';
import FuzzyMatchControl from './controls/FuzzyMatchControl.vue';

export default {
  name: 'RegularImportConfig',
  components: { MappedSelect, FuzzyMatchControl },

  props: {
    regularFields: {
      type: Array,
      required: true
    },
    foreignKeyFields: {
      type: Array,
      required: true
    },
    sheetColumns: {
      type: Array,
      required: true
    },
    identifierTypes: {
      type: Array,
      required: true
    },
    fieldMappings: {
      type: Object,
      required: true
    },
    foreignKeyMappings: {
      type: Object,
      required: true
    },
    relatedModelFieldsCache: {
      type: Object,
      required: true
    }
  },

  emits: ['update:field-mappings', 'update:foreign-key-mappings', 'load-related-fields'],

  methods: {
    updateFieldMapping(fieldName, value) {
      this.$emit('update:field-mappings', { ...this.fieldMappings, [fieldName]: value });
    },

    updateForeignKeyMapping(fieldName, property, value) {
      const updated = {
        ...this.foreignKeyMappings,
        [fieldName]: {
          ...this.foreignKeyMappings[fieldName],
          [property]: value
        }
      };
      this.$emit('update:foreign-key-mappings', updated);
    },

    relatedFieldOptions(fieldName) {
      return (this.relatedModelFieldsCache[fieldName] || []).map((f) => ({
        value: f.name,
        label: `${f.name} (${f.type})`,
      }));
    }
  }
};
</script>
